// Smart Sash Admin Panel - Hood Usage Report Table
// Sortable table with pagination

import React from 'react';
import { HoodUsageSession, HoodUsageSortField, HoodUsageSortState, PaginationMetadata } from '@/models';
import { ComplianceBadge } from './ComplianceBadge';

interface HoodUsageTableProps {
  sessions: HoodUsageSession[];
  sort: HoodUsageSortState;
  onSort: (field: HoodUsageSortField) => void;
  pagination: PaginationMetadata | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading: boolean;
}

interface ColumnDef {
  field: HoodUsageSortField;
  label: string;
}

const columns: ColumnDef[] = [
  { field: 'hoodId', label: 'Hood ID' },
  { field: 'hoodType', label: 'Type' },
  { field: 'userName', label: 'User' },
  { field: 'duration', label: 'Duration (min)' },
  { field: 'complianceScore', label: 'Sash IQ' },
  { field: 'avgCfm', label: 'Avg CFM (VAV)' },
  { field: 'estEnergyCost', label: 'Est. Energy Cost' },
];

function getSortIcon(field: HoodUsageSortField, sort: HoodUsageSortState): string {
  if (sort.field !== field) return 'fas fa-sort text-muted';
  return sort.order === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
}

export const HoodUsageTable: React.FC<HoodUsageTableProps> = ({
  sessions,
  sort,
  onSort,
  pagination,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
}) => {
  const totalCount = pagination?.totalCount ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const startRecord = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalCount);

  const pageNumbers: number[] = [];
  const maxVisiblePages = 5;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  startPage = Math.max(1, endPage - maxVisiblePages + 1);
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.field}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onClick={() => onSort(col.field)}
                  >
                    {col.label}{' '}
                    <i className={getSortIcon(col.field, sort)} style={{ fontSize: '0.75rem' }}></i>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                    Loading...
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4 text-muted">
                    No sessions found matching the current filters.
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.id}>
                    <td className="fw-medium">{session.hoodId}</td>
                    <td>
                      <span className={`badge bg-${session.hoodType === 'VAV' ? 'info' : 'secondary'}`}>
                        {session.hoodType}
                      </span>
                    </td>
                    <td>{session.userName}</td>
                    <td>{session.duration}</td>
                    <td>
                      <ComplianceBadge score={session.complianceScore} level={session.complianceLevel} />
                    </td>
                    <td>{session.avgCfm != null ? session.avgCfm : <span className="text-muted">N/A</span>}</td>
                    <td>
                      {session.estEnergyCost != null ? (
                        `$${session.estEnergyCost.toFixed(2)}`
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalCount > 0 && (
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">
                Showing {startRecord}–{endRecord} of {totalCount}
              </small>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(page - 1)}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((p) => (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => onPageChange(p)}>
                      {p}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => onPageChange(page + 1)}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};
